import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PackageCheck, Send, XCircle } from 'lucide-react';
import { PERMISSIONS, StockTransferStatus } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Drawer } from '@/components/ui/Drawer';
import { Input } from '@/components/ui/Input';
import { LoadingState } from '@/components/ui/LoadingState';
import { useBranch } from '@/hooks/useBranch';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorCode, apiErrorMessage } from '@/services/apiClient';
import {
  useCancelTransferMutation,
  useDispatchTransferMutation,
  useReceiveTransferMutation,
  useTransferQuery,
} from '../api/transfers.api';
import { TransferStatusBadge } from './TransferStatusBadge';

interface TransferDetailDrawerProps {
  transferId: string | null;
  onClose: () => void;
  onDone: (message: string) => void;
}

/**
 * One transfer, and whichever action its status allows.
 *
 * Dispatch is offered only at the source branch and receipt only at the destination, matching the
 * server's branch checks — showing a button the API will refuse is worse than showing none.
 */
export function TransferDetailDrawer({ transferId, onClose, onDone }: TransferDetailDrawerProps) {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const { activeBranch } = useBranch();

  const [counted, setCounted] = useState<Record<string, string>>({});
  const [cancelReason, setCancelReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: transfer, isPending } = useTransferQuery(transferId ?? undefined);
  const dispatch = useDispatchTransferMutation();
  const receive = useReceiveTransferMutation();
  const cancel = useCancelTransferMutation();

  useEffect(() => {
    setError(null);
    setCancelReason('');
    setIsCancelling(false);

    // Pre-fill with what is still outstanding, so a second receipt starts from the remainder.
    setCounted(
      Object.fromEntries(
        (transfer?.lines ?? []).map((line) => [line.id, String(line.outstandingQuantity)]),
      ),
    );
  }, [transfer]);

  if (!transferId) return null;

  const canTransfer = can(PERMISSIONS.INVENTORY_TRANSFER);
  const isDraft = transfer?.status === StockTransferStatus.DRAFT;
  // A partially received shipment is still open, so it accepts further receipts.
  const isReceivable =
    transfer?.status === StockTransferStatus.DISPATCHED ||
    transfer?.status === StockTransferStatus.PARTIALLY_RECEIVED;
  const atSource = activeBranch?.id === transfer?.sourceBranchId;
  const atDestination = activeBranch?.id === transfer?.destinationBranchId;

  // A named refusal, such as a stale screen or a count beyond what was sent, reads in the screen's language; anything else keeps the server's words or the generic message.
  const fail = (mutationError: unknown, fallback: string) => {
    const code = apiErrorCode(mutationError);
    const known = code ? t(`apiErrors.${code}`, { defaultValue: '' }) : '';
    setError(known || apiErrorMessage(mutationError) || fallback);
  };

  const handleDispatch = () => {
    if (!transfer) return;
    setError(null);

    dispatch.mutate(
      { id: transfer.id, version: transfer.version },
      {
        onSuccess: () =>
          onDone(t('inventory.transfers.dispatched', { reference: transfer.reference })),
        onError: (mutationError) => fail(mutationError, t('inventory.transfers.dispatchError')),
      },
    );
  };

  const handleReceive = (finalize: boolean) => {
    if (!transfer) return;
    setError(null);

    const lines = transfer.lines.map((line) => ({
      lineId: line.id,
      receivedQuantity: Number(counted[line.id] ?? 0),
    }));

    if (
      lines.some((line) => !Number.isInteger(line.receivedQuantity) || line.receivedQuantity < 0)
    ) {
      setError(t('inventory.validation.quantityInvalid'));
      return;
    }

    receive.mutate(
      { id: transfer.id, version: transfer.version, lines, finalize },
      {
        onSuccess: (result) => {
          // Staying open is not a completion, so it reads as progress rather than a result.
          if (result.status === StockTransferStatus.PARTIALLY_RECEIVED) {
            onDone(
              t('inventory.transfers.partiallyReceived', {
                reference: result.reference,
                count: result.totalOutstandingQuantity,
              }),
            );
            return;
          }

          onDone(
            result.hasVariance
              ? t('inventory.transfers.receivedWithVariance', { reference: result.reference })
              : t('inventory.transfers.received', { reference: result.reference }),
          );
        },
        onError: (mutationError) => fail(mutationError, t('inventory.transfers.receiveError')),
      },
    );
  };

  const handleCancel = () => {
    if (!transfer) return;
    setError(null);

    if (cancelReason.trim().length < 3) {
      setError(t('inventory.transfers.validation.reasonRequired'));
      return;
    }

    cancel.mutate(
      { id: transfer.id, version: transfer.version, reason: cancelReason.trim() },
      {
        onSuccess: () =>
          onDone(t('inventory.transfers.cancelled', { reference: transfer.reference })),
        onError: (mutationError) => fail(mutationError, t('inventory.transfers.cancelError')),
      },
    );
  };

  return (
    <Drawer
      isOpen={Boolean(transferId)}
      onClose={onClose}
      title={transfer?.reference ?? t('inventory.transfers.title')}
    >
      {isPending || !transfer ? (
        <LoadingState />
      ) : (
        <div className="space-y-4">
          {error ? <Alert tone="error">{error}</Alert> : null}

          <div className="flex items-center justify-between">
            <TransferStatusBadge status={transfer.status} />
            <span className="text-muted text-xs">
              {`${t('inventory.transfers.lineCount', { count: transfer.lineCount })} · ${t(
                'inventory.transfers.unitCount',
                { count: transfer.totalQuantity },
              )}`}
            </span>
          </div>

          <dl className="border-line grid grid-cols-2 gap-3 rounded-lg border p-3 text-sm">
            <div>
              <dt className="text-muted text-xs">{t('inventory.transfers.fields.source')}</dt>
              <dd className="text-ink">{transfer.sourceBranchName}</dd>
            </div>
            <div>
              <dt className="text-muted text-xs">{t('inventory.transfers.fields.destination')}</dt>
              <dd className="text-ink">{transfer.destinationBranchName}</dd>
            </div>
            {transfer.dispatchedByName ? (
              <div>
                <dt className="text-muted text-xs">
                  {t('inventory.transfers.fields.dispatchedBy')}
                </dt>
                <dd className="text-ink">{transfer.dispatchedByName}</dd>
              </div>
            ) : null}
            {transfer.receivedByName ? (
              <div>
                <dt className="text-muted text-xs">{t('inventory.transfers.fields.receivedBy')}</dt>
                <dd className="text-ink">{transfer.receivedByName}</dd>
              </div>
            ) : null}
          </dl>

          {transfer.notes ? <p className="text-muted text-sm">{transfer.notes}</p> : null}

          {transfer.isOpen ? (
            transfer.totalOutstandingQuantity > 0 ? (
              <Alert tone="info">
                {t('inventory.transfers.outstandingNotice', {
                  count: transfer.totalOutstandingQuantity,
                })}
              </Alert>
            ) : null
          ) : transfer.hasVariance ? (
            <Alert tone="warning">{t('inventory.transfers.varianceWarning')}</Alert>
          ) : null}

          <ul className="space-y-2">
            {transfer.lines.map((line) => (
              <li key={line.id} className="border-line rounded-lg border p-3">
                <p className="text-ink text-sm font-medium">{line.productName}</p>
                <p className="text-muted text-xs">
                  <span className="font-mono" dir="ltr">
                    {line.sku}
                  </span>
                  {line.batchNumber ? <span className="mx-1.5">· {line.batchNumber}</span> : null}
                </p>

                <div className="mt-2 flex items-center justify-between gap-2">
                  <span className="text-muted text-xs">
                    {t('inventory.transfers.sentQuantity', { count: line.quantity })}
                    {line.receivedQuantity !== null ? (
                      <span className="ms-1.5">
                        {'· '}
                        {t('inventory.transfers.receivedSoFar', {
                          count: line.receivedQuantity,
                        })}
                      </span>
                    ) : null}
                  </span>

                  {isReceivable && atDestination && canTransfer ? (
                    <div className="w-28">
                      <Input
                        type="number"
                        min={0}
                        value={counted[line.id] ?? ''}
                        aria-label={t('inventory.transfers.countedFor', { sku: line.sku })}
                        onChange={(event) =>
                          setCounted((current) => ({ ...current, [line.id]: event.target.value }))
                        }
                      />
                    </div>
                  ) : line.receivedQuantity !== null ? (
                    <ReceivedQuantity
                      received={line.receivedQuantity}
                      dispatched={line.quantity}
                      variance={line.variance ?? 0}
                      isOpen={transfer.isOpen}
                    />
                  ) : null}
                </div>
              </li>
            ))}
          </ul>

          {isDraft && atSource && canTransfer ? (
            isCancelling ? (
              <div className="space-y-2">
                <Input
                  value={cancelReason}
                  label={t('inventory.transfers.fields.cancelReason')}
                  onChange={(event) => setCancelReason(event.target.value)}
                />
                <div className="flex justify-end gap-2">
                  <Button variant="secondary" onClick={() => setIsCancelling(false)}>
                    {t('common.cancel')}
                  </Button>
                  <Button variant="danger" isLoading={cancel.isPending} onClick={handleCancel}>
                    {t('inventory.transfers.confirmCancel')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={() => setIsCancelling(true)}>
                  <XCircle className="size-4" aria-hidden />
                  {t('inventory.transfers.cancel')}
                </Button>
                <Button isLoading={dispatch.isPending} onClick={handleDispatch}>
                  <Send className="size-4" aria-hidden />
                  {t('inventory.transfers.dispatch')}
                </Button>
              </div>
            )
          ) : null}

          {isReceivable && atDestination && canTransfer ? (
            <>
              <Alert tone="info">{t('inventory.transfers.receiveHint')}</Alert>
              <div className="flex flex-wrap justify-end gap-2">
                <Button isLoading={receive.isPending} onClick={() => handleReceive(false)}>
                  <PackageCheck className="size-4" aria-hidden />
                  {t('inventory.transfers.receive')}
                </Button>
                <Button
                  variant="secondary"
                  isLoading={receive.isPending}
                  onClick={() => handleReceive(true)}
                >
                  {t('inventory.transfers.finalise')}
                </Button>
              </div>
              <p className="text-muted text-xs">{t('inventory.transfers.finaliseHint')}</p>
            </>
          ) : null}

          {isReceivable && !atDestination ? (
            <Alert tone="info">
              {t('inventory.transfers.switchToReceive', { branch: transfer.destinationBranchName })}
            </Alert>
          ) : null}

          {isDraft && !atSource ? (
            <Alert tone="info">
              {t('inventory.transfers.switchToDispatch', { branch: transfer.sourceBranchName })}
            </Alert>
          ) : null}
        </div>
      )}
    </Drawer>
  );
}

/**
 * The counted figure so far.
 *
 * While the transfer is open a difference is just progress, so it reads as "6 / 10". Once closed
 * the same difference is a shortfall someone accepted, and it is called out as a signed variance.
 */
function ReceivedQuantity({
  received,
  dispatched,
  variance,
  isOpen,
}: {
  received: number;
  dispatched: number;
  variance: number;
  isOpen: boolean;
}) {
  if (isOpen) {
    return (
      <span className="text-ink text-sm" dir="ltr">
        {received} / {dispatched}
      </span>
    );
  }

  return (
    <span
      className={variance === 0 ? 'text-ink text-sm' : 'text-sm font-semibold text-amber-700'}
      dir="ltr"
    >
      {received}
      {variance === 0 ? '' : ` (${variance > 0 ? '+' : ''}${variance})`}
    </span>
  );
}
